import { Injectable } from '@angular/core';
import { SessionEntity } from '@base/entities/BaseEntities';
import { BaseDataSourceAbstract } from '@base/datalayer/BaseDataSourceAbstract';
import { first, mergeMap, Subject, Subscription, tap, map, of } from 'rxjs';
import { SystemOptionsService } from './system-options.service';
import { EnumSystemOptions } from './systemoptions.enum';
import { AppConfigService } from '@m4u-angular/configuration/app.config.service';
import { NotificationService } from './notification.service';

export enum SessionStates {
    Started,
    Ended,
    AlmostExpired,
    Expired
};

@Injectable({
    providedIn: 'root'
})
export class SessionService {
    //Store server session
    private m_Session: SessionEntity;
    //Store user info
    private m_UserInfoDict: { [id: string]: any };
    //Store dataSources
    private m_DataSources: { [id: string]: BaseDataSourceAbstract };

    private sessionExpiredTimeoutHandler;
    private sessionAlmostExpiredTimeoutHandler;
    private initAppSubscription: Subscription;
    private logoutAppSubscription: Subscription;

    private sessionStateChangedSubject = new Subject<SessionStates>();
    readonly sessionStateChanged$ = this.sessionStateChangedSubject.asObservable();

    constructor(private systemOptionsService: SystemOptionsService,
        private appConfigService: AppConfigService,
        private notificationService: NotificationService) { }

    get IsLoggedIn(): boolean { return (this.m_Session != null); }
    get DisplayUserName(): string { return this.IsLoggedIn ? this.m_Session.DisplayUserName : ''; }
    get DisplayUserDescription(): string { return this.IsLoggedIn ? this.m_Session.DisplayUserDescription : ''; }
    get CurrentUserID(): number { return this.IsLoggedIn ? this.m_Session.CredentialID : undefined; }
    get SessionID(): string { return this.IsLoggedIn ? this.m_Session.SessionID : undefined; }
    get UserInfoDict(): { [id: string]: any } { return this.IsLoggedIn ? this.m_UserInfoDict : {}; }
    get UserInfos(): any[] { return this.IsLoggedIn ? Object.keys(this.m_UserInfoDict).map(x => this.m_UserInfoDict[x]) : []; }
    get DataSources(): { [id: string]: BaseDataSourceAbstract } { return this.IsLoggedIn ? this.m_DataSources : {}; }

    //Set session
    SetSession(oSession: SessionEntity,
        afterInitFn?: () => void,
        afterLogoutFn?: () => void) {
        this.m_Session = oSession;
        this.m_UserInfoDict = oSession ? oSession.UserInfo : undefined;

        this.RefreshExpiredSessionTimeout(!!oSession);

        if (!!oSession) {
            this.initAppSubscription && !this.initAppSubscription.closed && this.initAppSubscription.unsubscribe();

            const initApplication$ = this.appConfigService.AppConfig.OnInitApplicationAsync(this.m_Session.UserInfo)
                .pipe(
                    first(),
                    mergeMap((result) => {
                        if (result)
                            return this.appConfigService.AppConfig.OnGetGlobalDataSources();
                        else
                            throw 'Cannot start session.';
                    }),
                    tap(ds => this.SetDataSources(ds)),
                    tap(() => this.notificationService.Start()),
                    tap(() => { afterInitFn && afterInitFn(); }),
                    map(() => true)
                );
            this.initAppSubscription = initApplication$
                .subscribe({
                    next: x => {
                        this.sessionStateChangedSubject.next(SessionStates.Started);
                    }, error: e => {
                        this.sessionStateChangedSubject.next(SessionStates.Ended);
                    }
                });

            return initApplication$;
        }

        if (!oSession) {
            this.logoutAppSubscription && !this.logoutAppSubscription.closed && this.logoutAppSubscription.unsubscribe();

            const onLogoutapplication$ = this.appConfigService.AppConfig.OnLogoutApplicationAsync()
            .pipe(
                tap(() => {
                    this.ClearSessionCredentials();
                    this.SetDataSources(undefined);
                }),
                tap(() => this.notificationService.Stop()),
                tap(() => { afterLogoutFn && afterLogoutFn(); }),
                map(() => false)
            );
            this.logoutAppSubscription = onLogoutapplication$
                .subscribe(x => {
                    this.sessionStateChangedSubject.next(SessionStates.Ended);
                });
            return onLogoutapplication$;
        }
    }

    // Shortcut for end session action
    EndSession(afterFn?: () => void) {
        this.SetSession(undefined, null, afterFn);
    }

    //Set datasources
    SetDataSources(dataSources: { [id: string]: BaseDataSourceAbstract }) {
        this.m_DataSources = dataSources;
    }

    SaveSessionCredentials(sessionID: string) {
        localStorage.setItem("SessionID", sessionID);
    }

    // obsolete ??
    ClearSessionCredentials() {
        localStorage.removeItem("SessionID");
    }
    
    GetSessionID() {
        return localStorage.getItem("SessionID");
    }

    //Mark session as logged out 
    // obsolete
    // LogoutException() {
    //     //this.ClearSessionData();
    //     this.SetSession(null);
    // }

    //Mark session as logged out, and reset the saved session
    // obsolete
    // LogoutAndResetSession() {
    //     this.ClearSessionCredentials();
    //     this.ClearSessionData();
    // }

    //Clear all session data
    // obsolete
    // ClearSessionData() {
    //     this.SetSession(undefined);
    //     this.SetDataSources(undefined);
    // }

    RefreshExpiredSessionTimeout(startNew: boolean = true) {
        const sessionTimeout = +this.systemOptionsService.getSystemOption(EnumSystemOptions.SessionTimeout);
        if (!sessionTimeout || sessionTimeout < 0) return;

        const sessionAlmostTimeout = (sessionTimeout > 3) ? (sessionTimeout - 2) : 0;

        if (this.sessionExpiredTimeoutHandler) {
            clearTimeout(this.sessionExpiredTimeoutHandler);
            this.sessionExpiredTimeoutHandler = null;
        }

        if (this.sessionAlmostExpiredTimeoutHandler) {
            clearTimeout(this.sessionAlmostExpiredTimeoutHandler);
            this.sessionAlmostExpiredTimeoutHandler = null;
        }

        if (startNew && !!this.m_Session) {
            if (sessionAlmostTimeout) {
                this.sessionAlmostExpiredTimeoutHandler = setTimeout(() => {
                    this.sessionStateChangedSubject.next(SessionStates.AlmostExpired);
                }, sessionAlmostTimeout * 1000 * 60);
            }

            this.sessionExpiredTimeoutHandler = setTimeout(() => {
                this.AutoEndSession();
            }, sessionTimeout * 1000 * 60);
        }
    }

    private AutoEndSession() {
        this.RefreshExpiredSessionTimeout(false);
        //
        // TODO: END SESSION
        // IT should be full cleanup of session states (pre/post action, api calls, etc.)
        this.ClearSessionCredentials();
        //
        this.sessionStateChangedSubject.next(SessionStates.Expired);
    }
}
